import {
  IGetLrwSessionsFromLrwSessionServiceQuery,
  PagedResultOfLrwSessionDto,
  AvailableLrwSessionDateDto,
  VenueForLrwSessionDto,
  LrwSessionVenueDto,
  lrwSessionClient,
  LrwSessionDetailsDto,
  CreateLrwSessionRequest,
  LinkedSpeakingSessionDto,
  SpeakingSessionForLrwSessionDto,
  CreateAndLinkSpeakingSessionRequest,
  AssignedLrwRoomDto,
  AddRoomsToLrwSessionRequest,
  LrwRoomDto,
  UpdateSeatLimitRequest,
  LrwSessionSalesChannelAvailabilityDto,
  UpdateSalesChannelAvailabilityRequest,
  LrwSessionExamOverviewDto,
  LrwSessionUpdateDataDto,
  UpdateLrwSessionRequest,
} from 'api/planning';
import { api } from '../api';
import {
  LINKED_SPEAKING_SESSIONS_LIST_QUERY_TAG,
  LRW_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
  LRW_ASSIGNED_ROOMS_QUERY_TAG,
  LRW_AVAILABLE_ROOMS_QUERY_TAG,
  LRW_SESSION_DETAILS_QUERY_TAG,
  LRW_SESSION_EDIT_DATA_QUERY_TAG,
  LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG,
  SPEAKING_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
} from 'store';
import { LrwSessionTableState, LrwSessionsQueryFilters } from './types';
import { getSortingAndPaginationQuery, invalidateOn } from 'utils';

export const lrwSessionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAvailableVenuesForLrwSession: builder.query<
      VenueForLrwSessionDto[],
      { sessionDate: string; locationId: number }
    >({
      query: ({ sessionDate, locationId: _ }) => lrwSessionClient.getAvailableVenues(sessionDate),
    }),
    getAvailableDatesForLrwSession: builder.query<
      AvailableLrwSessionDateDto[],
      { locationId: number }
    >({
      query: ({ locationId: _ }) => lrwSessionClient.getAvailableDates(),
    }),
    createLrwSession: builder.mutation<number, CreateLrwSessionRequest>({
      query: (request) => lrwSessionClient.createLrwSession(request),
    }),
    getLrwSessionUpdateData: builder.query<LrwSessionUpdateDataDto, { lrwSessionId: number }>({
      query: ({ lrwSessionId }) => lrwSessionClient.getLrwSessionUpdateData(lrwSessionId),
      providesTags: () => [{ type: LRW_SESSION_EDIT_DATA_QUERY_TAG }],
    }),
    updateLrwSession: builder.mutation<
      void,
      { lrwSessionId: number; request: UpdateLrwSessionRequest }
    >({
      query: ({ lrwSessionId, request }) =>
        lrwSessionClient.updateLrwSession(lrwSessionId, request),
      invalidatesTags: invalidateOn({
        success: [
          LRW_SESSION_DETAILS_QUERY_TAG,
          LRW_SESSION_EDIT_DATA_QUERY_TAG,
          LRW_AVAILABLE_ROOMS_QUERY_TAG,
        ],
      }),
    }),
    getLrwSessions: builder.query<
      PagedResultOfLrwSessionDto,
      {
        tableState: LrwSessionTableState;
        filters?: LrwSessionsQueryFilters;
        locationId: number | undefined;
      }
    >({
      query: ({ tableState, filters, locationId: _ }) => {
        const query: IGetLrwSessionsFromLrwSessionServiceQuery = {
          ...getSortingAndPaginationQuery(tableState),
          filter: filters,
        };

        return lrwSessionClient.getLrwSessions(query);
      },
    }),
    getLrwSessionVenues: builder.query<LrwSessionVenueDto[], { locationId: number | undefined }>({
      query: ({ locationId: _ }) => lrwSessionClient.getVenues(),
    }),
    getLrwSessionDetails: builder.query<
      LrwSessionDetailsDto,
      { lrwSessionId: number; locationId: number | undefined }
    >({
      query: ({ lrwSessionId, locationId: _ }) =>
        lrwSessionClient.getLrwSessionDetails(lrwSessionId, {
          customErrorHandling: (response) => response.status === 404,
        }),
      providesTags: () => [{ type: LRW_SESSION_DETAILS_QUERY_TAG }],
    }),
    getLinkedSpeakingSessions: builder.query<LinkedSpeakingSessionDto[], { lrwSessionId: number }>({
      query: ({ lrwSessionId }) => lrwSessionClient.getLinkedSessions(lrwSessionId),
      providesTags: () => [{ type: LINKED_SPEAKING_SESSIONS_LIST_QUERY_TAG }],
    }),
    getAvailableSpeakingSessions: builder.query<
      SpeakingSessionForLrwSessionDto[],
      { lrwSessionId: number }
    >({
      query: ({ lrwSessionId }) => lrwSessionClient.getAvailableSpeakingSessions(lrwSessionId),
    }),
    getLrwSessionAssignedRooms: builder.query<AssignedLrwRoomDto[], { lrwSessionId: number }>({
      query: ({ lrwSessionId }) => lrwSessionClient.getAssignedRooms(lrwSessionId),
      providesTags: () => [{ type: LRW_ASSIGNED_ROOMS_QUERY_TAG }],
    }),
    getLrwSessionAvailableRooms: builder.query<
      LrwRoomDto[],
      { lrwSessionId: number; agentId?: number | null }
    >({
      query: ({ lrwSessionId, agentId }) =>
        lrwSessionClient.getAvailableRoomForSession(lrwSessionId, agentId),
      providesTags: () => [{ type: LRW_AVAILABLE_ROOMS_QUERY_TAG }],
    }),
    getSalesChannelAvailability: builder.query<
      LrwSessionSalesChannelAvailabilityDto,
      { lrwSessionId: number }
    >({
      query: ({ lrwSessionId }) => lrwSessionClient.getSalesChannelAvailability(lrwSessionId),
    }),
    assignRoomsToLrwSession: builder.mutation<
      void,
      { lrwSessionId: number; request: AddRoomsToLrwSessionRequest }
    >({
      query: ({ lrwSessionId, request }) =>
        lrwSessionClient.assignRoomsToSession(lrwSessionId, request),
      invalidatesTags: invalidateOn({
        success: [
          LRW_ASSIGNED_ROOMS_QUERY_TAG,
          LRW_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
          LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG,
        ],
        error: [LRW_AVAILABLE_ROOMS_QUERY_TAG],
      }),
    }),
    linkSpeakingSession: builder.mutation<
      number,
      { lrwSessionId: number; speakingSessionId: number }
    >({
      query: ({ lrwSessionId, speakingSessionId }) =>
        lrwSessionClient.linkSpeakingSession(lrwSessionId, speakingSessionId),
      invalidatesTags: invalidateOn({
        success: [
          LINKED_SPEAKING_SESSIONS_LIST_QUERY_TAG,
          LRW_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
          LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG,
        ],
      }),
    }),
    unLinkSpeakingSession: builder.mutation<
      void,
      { lrwSessionId: number; speakingSessionId: number }
    >({
      query: ({ lrwSessionId, speakingSessionId }) =>
        lrwSessionClient.unlinkSpeakingSession(lrwSessionId, speakingSessionId),
      invalidatesTags: invalidateOn({
        success: [
          LINKED_SPEAKING_SESSIONS_LIST_QUERY_TAG,
          LRW_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
          LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG,
        ],
      }),
    }),
    createAndLinkSpeakingSession: builder.mutation<
      number,
      { lrwSessionId: number; request: CreateAndLinkSpeakingSessionRequest }
    >({
      query: ({ lrwSessionId, request }) =>
        lrwSessionClient.createAndLinkSpeakingSession(lrwSessionId, request),
      invalidatesTags: invalidateOn({
        success: [
          LINKED_SPEAKING_SESSIONS_LIST_QUERY_TAG,
          LRW_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
          LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG,
        ],
      }),
    }),
    releaseLrwSession: builder.mutation<void, { lrwSessionId: number }>({
      query: ({ lrwSessionId }) => lrwSessionClient.releaseSession(lrwSessionId),
      invalidatesTags: invalidateOn({
        success: [
          LRW_SESSION_DETAILS_QUERY_TAG,
          LRW_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
          LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG,
          LRW_SESSION_EDIT_DATA_QUERY_TAG,
        ],
      }),
    }),
    releaseLrwWithSpeakingSession: builder.mutation<void, { lrwSessionId: number }>({
      query: ({ lrwSessionId }) => lrwSessionClient.releaseSessionWithSpeaking(lrwSessionId),
      invalidatesTags: invalidateOn({
        success: [
          LRW_SESSION_DETAILS_QUERY_TAG,
          LRW_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
          LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG,
          LRW_SESSION_EDIT_DATA_QUERY_TAG,
          SPEAKING_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
        ],
      }),
    }),
    updateLrwSessionRoomSeatLimit: builder.mutation<
      void,
      { lrwSessionId: number; roomId: number; request: UpdateSeatLimitRequest }
    >({
      query: ({ lrwSessionId, roomId, request }) =>
        lrwSessionClient.updateLrwRoomSeatsLimit(lrwSessionId, roomId, request),
      invalidatesTags: invalidateOn({
        success: [LRW_ASSIGNED_ROOMS_QUERY_TAG, LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG],
      }),
    }),
    updateSalesChannelAvailability: builder.mutation<
      void,
      { lrwSessionId: number; request: UpdateSalesChannelAvailabilityRequest }
    >({
      query: ({ lrwSessionId, request }) =>
        lrwSessionClient.updateSalesChannelAvailability(lrwSessionId, request),
    }),
    removeLrwSession: builder.mutation<void, { lrwSessionId: number }>({
      query: ({ lrwSessionId }) => lrwSessionClient.removeSession(lrwSessionId),
    }),
    removeLrwSessionRoom: builder.mutation<
      void,
      { lrwSessionId: number; lrwSessionRoomId: number }
    >({
      query: ({ lrwSessionId, lrwSessionRoomId }) =>
        lrwSessionClient.removeLrwSessionRoom(lrwSessionId, lrwSessionRoomId),
      invalidatesTags: invalidateOn({
        success: [
          LRW_ASSIGNED_ROOMS_QUERY_TAG,
          LRW_SESSION_ALLOWED_ACTIONS_QUERY_TAG,
          LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG,
        ],
      }),
    }),
    getLrwSessionExamOverview: builder.query<LrwSessionExamOverviewDto, { lrwSessionId: number }>({
      query: ({ lrwSessionId }) =>
        lrwSessionClient.getLrwSessionExamOverview(lrwSessionId, {
          customErrorHandling: (response) => response.status === 404,
        }),
      providesTags: () => [{ type: LRW_SESSION_EXAM_OVERVIEW_QUERY_TAG }],
    }),
  }),
});

export const {
  useGetAvailableVenuesForLrwSessionQuery,
  useCreateLrwSessionMutation,
  useGetLrwSessionUpdateDataQuery,
  useUpdateLrwSessionMutation,
  useGetLrwSessionsQuery,
  useGetLrwSessionVenuesQuery,
  useGetAvailableDatesForLrwSessionQuery,
  useGetLrwSessionDetailsQuery,
  useGetLinkedSpeakingSessionsQuery,
  useGetAvailableSpeakingSessionsQuery,
  useLinkSpeakingSessionMutation,
  useGetLrwSessionAssignedRoomsQuery,
  useGetLrwSessionAvailableRoomsQuery,
  useGetSalesChannelAvailabilityQuery,
  useAssignRoomsToLrwSessionMutation,
  useCreateAndLinkSpeakingSessionMutation,
  useReleaseLrwSessionMutation,
  useReleaseLrwWithSpeakingSessionMutation,
  useUpdateLrwSessionRoomSeatLimitMutation,
  useUpdateSalesChannelAvailabilityMutation,
  useRemoveLrwSessionMutation,
  useRemoveLrwSessionRoomMutation,
  useUnLinkSpeakingSessionMutation,
  useGetLrwSessionExamOverviewQuery,
} = lrwSessionApi;
